import { UIBlockElement } from 'common-ts/dist/models/Product';

// Product-detail layouts: which blocks a product page is made of, and the
// rules that keep a merchant-authored layout sellable.
//
// A product points at a ProductTemplate (Product.productTemplateId); a null
// pointer means the store's default template. Templates are fetched once per
// catalog load rather than inlined per product — a store can hold 350+.

export const PRODUCT_GALLERY_CODE = 'BlockProductGallery';
export const PRODUCT_TITLE_CODE = 'BlockProductTitlePrice';
export const PRODUCT_BUYBOX_CODE = 'BlockProductBuyBox';

// Blocks a product page can never be without. The gallery is additionally
// pinned to index 0; the other two may sit anywhere.
export const REQUIRED_PRODUCT_CODES = [
  PRODUCT_GALLERY_CODE,
  PRODUCT_TITLE_CODE,
  PRODUCT_BUYBOX_CODE,
];

export interface ProductTemplateLite {
  id: string;
  name?: string;
  elements: UIBlockElement[];
  isDefault: boolean;
}

// Mobile and desktop start symmetric; the merchant splits them in the
// editor. Each device gets its OWN content object — sharing one would make
// an edit to either device silently mutate both.
function block(code: string, content: Record<string, unknown> = {}): UIBlockElement {
  return {
    elementDesktopCode: code,
    elementDesktopContentJson: { ...content },
    elementMobileCode: code,
    elementMobileContentJson: { ...content },
  };
}

// The built-in layout, used when a store has no templates at all. Must stay
// in sync with DEFAULT_PRODUCT_LAYOUT in the backend
// (terbebas-backend/src/service/productLayoutDefault.ts), which seeds every
// new store's "Standar" template — drift means a fresh store renders
// differently from a store that predates templates.
//
// fullWidth lifts a block out of the 30% desktop rail into a full-width
// stack below the gallery/rail row. Ignored on mobile (single column).
export function defaultProductLayout(): UIBlockElement[] {
  return [
    block(PRODUCT_GALLERY_CODE),
    block(PRODUCT_TITLE_CODE),
    block(PRODUCT_BUYBOX_CODE),
    block('BlockProductDescription'),
    block('BlockProductReviews', { fullWidth: true }),
    block('BlockProductRelated', { fullWidth: true }),
  ];
}

function codesOf(entry: any): string[] {
  return [entry?.elementDesktopCode, entry?.elementMobileCode].filter(Boolean);
}

// A block "is" a required type if EITHER device renders it — otherwise a
// merchant could dodge a lock by setting one device to 'Empty'.
function isCode(entry: any, code: string): boolean {
  return codesOf(entry).indexOf(code) !== -1;
}

// Repairs a layout so it always renders a sellable page. Applied on render
// (defensively) and in the editor on load and on save, so a template
// authored before a rule existed heals itself the next time it is opened.
//
// Returns the ORIGINAL array reference when nothing needed changing, so
// React memo comparisons and the dashboard's dirty-checking don't churn.
export function normalizeProductLayout(elements: any): UIBlockElement[] {
  const input: any[] = Array.isArray(elements) ? elements : [];
  let changed = !Array.isArray(elements);

  // 1. Drop junk entries.
  const cleaned = input.filter((e) => {
    const ok = e && typeof e === 'object';
    if (!ok) changed = true;
    return ok;
  });

  // 2. Keep only the first occurrence of each required block.
  const seen: { [code: string]: boolean } = {};
  const deduped = cleaned.filter((entry) => {
    for (const code of REQUIRED_PRODUCT_CODES) {
      if (!isCode(entry, code)) continue;
      if (seen[code]) { changed = true; return false; }
      seen[code] = true;
    }
    return true;
  });

  // 3. A required block must render on BOTH devices — never 'Empty'.
  const forced = deduped.map((entry) => {
    const code = REQUIRED_PRODUCT_CODES.filter((c) => isCode(entry, c))[0];
    if (!code) return entry;
    if (entry.elementDesktopCode === code && entry.elementMobileCode === code) return entry;
    changed = true;
    return {
      ...entry,
      elementDesktopCode: code,
      elementMobileCode: code,
      // Content is per-device; a device that was hidden has none to keep.
      elementDesktopContentJson: entry.elementDesktopCode === code
        ? entry.elementDesktopContentJson : {},
      elementMobileContentJson: entry.elementMobileCode === code
        ? entry.elementMobileContentJson : {},
    };
  });

  // 4. Gallery is pinned to index 0.
  let result = forced;
  const galleryAt = result.findIndex((e) => isCode(e, PRODUCT_GALLERY_CODE));
  if (galleryAt === -1) {
    result = [block(PRODUCT_GALLERY_CODE), ...result];
    changed = true;
  } else if (galleryAt > 0) {
    result = [result[galleryAt], ...result.filter((_, i) => i !== galleryAt)];
    changed = true;
  }

  // 5. Title, then buy box — inserted right after the gallery when missing,
  //    so a repaired page reads top-to-bottom the way the default does.
  if (!result.some((e) => isCode(e, PRODUCT_TITLE_CODE))) {
    result = [result[0], block(PRODUCT_TITLE_CODE), ...result.slice(1)];
    changed = true;
  }
  if (!result.some((e) => isCode(e, PRODUCT_BUYBOX_CODE))) {
    const titleAt = result.findIndex((e) => isCode(e, PRODUCT_TITLE_CODE));
    result = [
      ...result.slice(0, titleAt + 1),
      block(PRODUCT_BUYBOX_CODE),
      ...result.slice(titleAt + 1),
    ];
    changed = true;
  }

  return (changed ? result : elements) as UIBlockElement[];
}

// Resolution order: the product's own template, then the store default,
// then the built-in layout. An empty `elements` array counts as unset so a
// merchant can't end up with a blank product page.
export function resolveProductLayout(
  productTemplateId: string | null | undefined,
  templatesById: { [id: string]: ProductTemplateLite },
  defaultTemplateId: string | null | undefined,
): UIBlockElement[] {
  const explicit = productTemplateId ? templatesById[productTemplateId] : undefined;
  const fallback = defaultTemplateId ? templatesById[defaultTemplateId] : undefined;
  const elements =
    explicit?.elements?.length ? explicit.elements
      : fallback?.elements?.length ? fallback.elements
        : defaultProductLayout();
  return normalizeProductLayout(elements);
}
